import { inject, ref } from 'vue'
import { defineStore } from 'pinia'
import { RequestService } from '../services/RequestService'
import { getAuth } from 'firebase/auth'
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore'
import { getAnswerForQuestion, parseAnswerToArray, normalizeAnswerForScore } from '../utils/questionsAndAnswersHelpers'
import {
  isActivePlanYearDate as isActivePlanYearDateHelper,
  getActivePlanYearDates as getActivePlanYearDatesHelper,
  getGroupedPlanYearStartDates as getGroupedPlanYearStartDatesHelper
} from '../utils/employersHelpers'
import { AgencyDataService } from '../services/AgencyDataService'
import { getAccountOwners, normalizeOwnerEmail } from '../utils/accountOwnersHelpers'
import { BOOK_VIEWS_LIST, DEFAULT_VIEW } from '../components/ActionCenter/constants'
import { FIRESTORE_IN_QUERY_LIMIT } from '../constants/firestoreConstants'
import navigationMonitor from '../services/NavigationMonitorService'

const { simpleGet, handleErrors } = RequestService()

/**
 * Merges the shared assessment questions base (question metadata, no answers) with
 * per-plan-year answer fields to produce the assessmentQuestionsData structure expected
 * by ScoreCalculationsService and ActionCenterOffCanvas.
 * @param {Object} questionsBase - Grouped questions: { sectionTitle: [questionObjects] }
 * @param {Object} assessmentAnswers - Per-plan-year map: { questionId: { answer, answer_id, notes, ... } }
 * @returns {Object} - { sectionTitle: [questionObjects with answer fields attached] }
 */
function mergeAssessmentQuestionsWithAnswers(questionsBase, assessmentAnswers) {
  if (!questionsBase) return {}
  const answersLookup = assessmentAnswers || {}
  const result = {}
  for (const [sectionTitle, questions] of Object.entries(questionsBase)) {
    result[sectionTitle] = questions.map(question => {
      const answerRecord = answersLookup[question.id]
      const rawAnswer = answerRecord?.answer
      return {
        ...question,
        answer: normalizeAnswerForScore(rawAnswer, question),
        answer_id: answerRecord?.answer_id ?? null,
        notes: answerRecord?.notes ?? '',
        note_updated_at: answerRecord?.note_updated_at ?? null,
        note_updated_by: answerRecord?.note_updated_by ?? null,
        note_updated_by_name: answerRecord?.note_updated_by_name ?? ''
      }
    })
  }
  return result
}

const ALL_CACHE_TTL_MS = 2 * 60 * 60 * 1000 // 2 hours

export const useActionCenterStore = defineStore('actionCenter', () => {
  const functionsBaseUrl = inject('functionsBaseUrl')
  
  // State
  const accountOwners = ref([])
  const accountOwnerPlanYears = ref([]) // All plan years for selected account owner's employers
  const filteredPlanYears = ref([]) // Filtered plan years based on selected filters
  const planYearStartDates = ref([])
  const erisaPlans = ref([])
  const medicalPlans = ref([])
  const coveragesOptions = ref([])
  const isLoadingFilters = ref(false)
  const isLoadingPlanYears = ref(true)

  // Reports to Sentry when the Action Center stays loading past the timeout
  // (e.g. after being restored via browser Back/Forward).
  navigationMonitor.watchLoading('actionCenter', isLoadingPlanYears)
  const showHighRiskDollarView = ref(false) // Toggle for High-Risk card view
  const lastRemindersSummary = ref(null)
  const remindersBannerVisible = ref(false) // Whether the reminders sent banner is currently visible
  const shareUpdateEmailSent = ref(false) // Whether a share-update email was just sent successfully
  const wasNext30DaysFilterActive = ref(false) // Flag to track if reminders were sent with 30-day filter
  const historicalReminders = ref([]) // Reminders sent historically for filtered plan years
  const isLoadingHistoricalReminders = ref(false)
  const savedScrollPosition = ref(0) // Scroll position to restore when returning to Action Center
  const savedDeadlinesSelection = ref(null) // { groupBy, selectedKeys: [...] } persisted across view switches
  const bookDetailsView = ref(DEFAULT_VIEW)
  const accountOwnerCountsRequestId = ref(0)
  const currentAgencyId = ref(null) // Agency ID of the current user (set during loadAccountOwners)
  const localDataLoadTimestamp = ref(null) // Timestamp of last successful data load from backend
  const _allPlanYearsCache = ref([]) // Full "all account owners" plan years, used to derive subsets without re-fetching
  const _allPlanYearsCacheLoadedAt = ref(0) // Timestamp of last successful "all" fetch
  
  // Filter states - Grouped in a single reactive object
  // accountOwner is an array: owner emails and/or the special tokens 'all' / 'unassigned'
  const selectedFilters = ref({
    accountOwner: [],
    planYearStartDate: [],
    erisaPlan: [],
    fullTimeEmployees: '',
    medicalPlan: [],
    coverages: []
  })

  /**
   * Get the current authenticated user's email
   * @returns {string|null} User email or null if not authenticated
   */
  const getCurrentUserEmail = () => {
    const auth = getAuth()
    // Normalized so it can be compared against the owner options, whose values come
    // from `users.email` and may carry legacy mixed casing.
    return auth.currentUser ? normalizeOwnerEmail(auth.currentUser.email) : null
  }

  const resetBookDetailsView = () => {
    bookDetailsView.value = DEFAULT_VIEW
  }

  const setBookDetailsView = (view) => {
    if (BOOK_VIEWS_LIST.includes(view)) {
      bookDetailsView.value = view
    } else {
      handleErrors('Invalid view type', new Error(`Invalid view: ${view}`))
      resetBookDetailsView()
    }
  }

  const applyAccountOwnerPlanYearCounts = (countsByOwner, allCount) => {
    accountOwners.value = accountOwners.value.map(option => {
      const baseLabel = option.baseLabel || option.label
      let count = 0

      if (option.value === 'all') {
        count = allCount
      } else if (option.value === 'unassigned') {
        count = countsByOwner.unassigned || 0
      } else {
        count = countsByOwner[option.value] || 0
      }

      return {
        ...option,
        count,
        label: `${baseLabel} (${count})`
      }
    })
  }

  const loadAccountOwnerPlanYearCounts = async (agencyId) => {
    const requestId = accountOwnerCountsRequestId.value + 1
    accountOwnerCountsRequestId.value = requestId

    try {
      const db = getFirestore()
      const employersRef = collection(db, 'employers')
      const employersQuery = query(employersRef, where('agency_id', '==', agencyId))
      const employersSnapshot = await getDocs(employersQuery)

      if (requestId !== accountOwnerCountsRequestId.value) {
        return
      }

      if (employersSnapshot.empty) {
        applyAccountOwnerPlanYearCounts({}, 0)
        return
      }

      const employerIds = []
      const ownersByEmployerId = new Map()

      employersSnapshot.docs.forEach(doc => {
        const owners = getAccountOwners(doc.data())
        employerIds.push(doc.id)
        ownersByEmployerId.set(doc.id, owners.length > 0 ? owners : ['unassigned'])
      })

      const planYearsRef = collection(db, 'employer_plan_years')

      // Fetch plan year counts in parallel batches
      const batches = []
      for (let i = 0; i < employerIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
        batches.push(employerIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT))
      }

      const snapshots = await Promise.all(
        batches.map(batch => getDocs(query(
          planYearsRef,
          where('employer_id', 'in', batch),
          where('agency_id', '==', agencyId)
        )))
      )

      if (requestId !== accountOwnerCountsRequestId.value) {
        return
      }

      // A multi-owner employer's plan years count once toward EACH of its owners;
      // the 'all' count stays a unique plan-year total.
      const countsByOwner = {}
      let allCount = 0
      snapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
          const data = doc.data()
          const ownerKeys = ownersByEmployerId.get(data.employer_id) || ['unassigned']
          ownerKeys.forEach(ownerKey => {
            countsByOwner[ownerKey] = (countsByOwner[ownerKey] || 0) + 1
          })
          allCount++
        })
      })

      applyAccountOwnerPlanYearCounts(countsByOwner, allCount)
    } catch (error) {
      if (requestId !== accountOwnerCountsRequestId.value) {
        return
      }

      handleErrors('Failed to load account owner plan year counts', error)
    }
  }

  // Wrapper functions that use helpers with current state
  const isActivePlanYearDate = (dateStr) => isActivePlanYearDateHelper(dateStr)
  const getActivePlanYearDates = () => getActivePlanYearDatesHelper(planYearStartDates.value)
  const getGroupedPlanYearStartDates = () => getGroupedPlanYearStartDatesHelper(planYearStartDates.value)

  /**
   * Load all users from the same agency as the authenticated user
   * @returns {Promise<void>}
   */
  const loadAccountOwners = async () => {
    try {
      const auth = getAuth()
      const db = getFirestore()
      const currentUser = auth.currentUser

      if (!currentUser) {
        handleErrors('Failed to load account owners', new Error('No authenticated user'))
        accountOwners.value = []
        return
      }

      // 1. Get current user document
      const usersRef = collection(db, 'users')
      const currentUserQuery = query(usersRef, where('__name__', '==', currentUser.uid))
      const currentUserSnapshot = await getDocs(currentUserQuery)
      
      if (currentUserSnapshot.empty) {
        handleErrors('Failed to load account owners', new Error('Current user not found in users collection'))
        accountOwners.value = []
        return
      }

      const currentUserData = currentUserSnapshot.docs[0].data()
      const agencyId = currentUserData.agency_id

      if (!agencyId) {
        handleErrors('ActionCenterStore.loadAccountOwners', new Error('Current user has no agency_id'))
        accountOwners.value = []
        return
      }

      currentAgencyId.value = agencyId

      const ownersResponse = await simpleGet(
        'loadAccountOwners',
        `${functionsBaseUrl}/usersGateway/api/v1/users/account-owners`,
        { rethrow: true }
      )
      const currentUserEmail = normalizeOwnerEmail(currentUser.email)
      const owners = (ownersResponse?.data?.data || []).map(owner => ({
        value: owner.email,
        label: owner.fullname,
        baseLabel: owner.fullname,
        count: null
      }))

      // Sort alphabetically by label
      owners.sort((a, b) => a.label.localeCompare(b.label))

      // 4. Move current user to the top, then add All and Unassigned after
      const currentUserIndex = owners.findIndex(o => o.value === currentUserEmail)
      let currentUserOption = null
      if (currentUserIndex !== -1) {
        currentUserOption = owners.splice(currentUserIndex, 1)[0]
      }

      const specialOptions = [
        { value: 'all', label: 'All Account Owners', baseLabel: 'All Account Owners', count: null },
        { value: 'unassigned', label: 'Unassigned', baseLabel: 'Unassigned', count: null }
      ]

      if (currentUserOption) {
        owners.unshift(currentUserOption, ...specialOptions)
      } else {
        owners.unshift(...specialOptions)
      }

      accountOwners.value = owners
      void loadAccountOwnerPlanYearCounts(agencyId)
      
    } catch (error) {
      handleErrors('Failed to load account owners', error)
      accountOwners.value = []
    }
  }

  /**
   * Apply all selected filters to the plan years
   */
  const applyFilters = () => {
    const filtered = accountOwnerPlanYears.value.filter(py => {
      // Filter 1: Plan Year Start Date (now supports multiple selection)
      const selectedDates = selectedFilters.value.planYearStartDate
      if (selectedDates && selectedDates.length > 0) {
        if (!selectedDates.includes(py.plan_start_date)) return false
      }

      // Filter 2: ERISA Plan (question_id: "8", answer: "0" or "1")
      if (selectedFilters.value.erisaPlan.length > 0) {
        const answer = getAnswerForQuestion(py, '8')
        if (!answer) return false
        
        const answerValue = parseInt(answer)
        if (!selectedFilters.value.erisaPlan.includes(answerValue)) return false
      }

      // Filter 3: Medical Plan (question_id: "11", answer: "0", "1", "2", "3")
      if (selectedFilters.value.medicalPlan.length > 0) {
        const answer = getAnswerForQuestion(py, '11')
        if (!answer) return false
        
        const answerValue = parseInt(answer)
        if (!selectedFilters.value.medicalPlan.includes(answerValue)) return false
      }

      // Filter 4: Coverages (question_id: "12", answer: "[1,2,3]")
      if (selectedFilters.value.coverages.length > 0) {
        const answer = getAnswerForQuestion(py, '12')
        if (!answer) return false
        
        const answerArray = parseAnswerToArray(answer)
        
        // Check if any selected coverage is in the answer array
        const hasMatchingCoverage = selectedFilters.value.coverages.some(selectedValue => 
          answerArray.includes(selectedValue)
        )
        if (!hasMatchingCoverage) return false
      }

      // Filter 5: Full-Time Employees (question_id: "9")
      // Only accept explicit range formatted as "min - max"; otherwise skip filter
      if (selectedFilters.value.fullTimeEmployees && selectedFilters.value.fullTimeEmployees.trim() !== '') {
        const fteRaw = selectedFilters.value.fullTimeEmployees.trim()
        const match = fteRaw.match(/^(\d+)\s*-\s*(\d+)$/)
        if (match) {
          let min = parseInt(match[1], 10)
          let max = parseInt(match[2], 10)
          if (min > max) { const t = min; min = max; max = t }

          const answer = getAnswerForQuestion(py, '9')
          if (!answer) return false
          const answerValue = parseInt(answer)
          if (isNaN(answerValue)) return false
          if (!(answerValue >= min && answerValue <= max)) return false
        }
      }

      // Passed all filters
      return true
    })

    filteredPlanYears.value = filtered
    
    console.log(`Applied filters: ${accountOwnerPlanYears.value.length} -> ${filtered.length} plan years`)
  }

  /**
   * Load plan years for the selected account owners' employers.
   * Selection is an array of owner emails and/or the special tokens 'all' / 'unassigned';
   * multiple values combine with OR semantics and results are deduped by plan year id.
   * @param {string[]|string} ownerSelection - Selected owner values (a lone string is accepted defensively)
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.forceReload=false] - Skip cache and fetch fresh data from backend
   * @returns {Promise<void>}
   */
  const loadPlanYearsByAccountOwner = async (ownerSelection, { forceReload = false } = {}) => {
    // Normalize up front so the 'all'/'unassigned' tokens, the owner emails and the
    // values read from employer docs are all comparable with `includes`.
    const selection = (Array.isArray(ownerSelection) ? ownerSelection : [ownerSelection])
      .map(value => normalizeOwnerEmail(value))
      .filter(Boolean)
    if (selection.length === 0) {
      handleErrors('loadPlanYearsByAccountOwner', new Error('No account owner selection provided'))
      accountOwnerPlanYears.value = []
      planYearStartDates.value = []
      return
    }
    const selectionLabel = selection.join(',')
    const includesAll = selection.includes('all')
    const includesUnassigned = selection.includes('unassigned')
    const selectedEmails = selection.filter(v => v !== 'all' && v !== 'unassigned')

    const matchesSelection = (py) => {
      if (includesAll) return true
      const owners = getAccountOwners(py.employer)
      if (includesUnassigned && owners.length === 0) return true
      return owners.some(owner => selectedEmails.includes(owner))
    }

    // Invalidate stale cache when a forced reload was requested
    if (forceReload) {
      _allPlanYearsCache.value = []
      _allPlanYearsCacheLoadedAt.value = 0
    }

    // If "all" was fetched recently, derive the requested subset without hitting the API
    const now = Date.now()
    const cacheIsFresh = _allPlanYearsCache.value.length > 0 &&
      (now - _allPlanYearsCacheLoadedAt.value) < ALL_CACHE_TTL_MS

    if (cacheIsFresh) {
      // Filtering a unique-by-id cache: dedupe is inherent
      const derived = _allPlanYearsCache.value.filter(matchesSelection)

      accountOwnerPlanYears.value = derived

      const uniqueDates = [...new Set(derived.map(py => py.plan_start_date))]
        .filter(date => date)
        .sort()

      planYearStartDates.value = uniqueDates.map(date => ({ value: date, label: date }))

      const currentSelection = selectedFilters.value.planYearStartDate || []
      const validCurrentSelection = currentSelection.filter(date => uniqueDates.includes(date))
      if (validCurrentSelection.length > 0) {
        selectedFilters.value.planYearStartDate = validCurrentSelection
      } else {
        selectedFilters.value.planYearStartDate = getActivePlanYearDates()
      }

      applyFilters()
      isLoadingPlanYears.value = false
      console.log(`Served ${derived.length} plan years from cache (${selectionLabel})`)
      return
    }

    isLoadingPlanYears.value = true

    try {
      const emailsParam = includesAll ? 'all' : selection.join(',')
      const url = `${functionsBaseUrl}/actionCenterGateway/api/v1/action-center/plan-years?accountOwnerEmails=${encodeURIComponent(emailsParam)}`
      const response = await simpleGet('getPlanYearsByAccountOwner', url)

      if (response?.data?.success) {
        const { planYears, assessmentQuestionsBase } = response.data.data

        // Dedupe by plan year id in case the backend ever merges per-owner result sets
        const seenIds = new Set()
        accountOwnerPlanYears.value = planYears.filter(py => {
          if (seenIds.has(py.id)) return false
          seenIds.add(py.id)
          return true
        }).map(py => {
          const { assessmentAnswers, ...rest } = py
          return {
            ...rest,
            assessmentQuestionsData: mergeAssessmentQuestionsWithAnswers(assessmentQuestionsBase, assessmentAnswers)
          }
        })

        // Extract unique plan_start_date values
        const uniqueDates = [...new Set(planYears.map(py => py.plan_start_date))]
          .filter(date => date)
          .sort() // Sort chronologically

        planYearStartDates.value = uniqueDates.map(date => ({
          value: date,
          label: date
        }))

        // Set default selection to all active dates
        // Preserve previously selected dates if any are still valid
        const currentSelection = selectedFilters.value.planYearStartDate || []
        const validCurrentSelection = currentSelection.filter(date => uniqueDates.includes(date))
        
        if (validCurrentSelection.length > 0) {
          // Keep valid previous selections
          selectedFilters.value.planYearStartDate = validCurrentSelection
        } else {
          // Default to all active dates
          selectedFilters.value.planYearStartDate = getActivePlanYearDates()
        }

        // Apply filters to the newly loaded plan years
        applyFilters()

        // Mark the timestamp of this successful load for cache comparison
        localDataLoadTimestamp.value = Date.now()

        // Populate "all" cache so subsequent specific-owner selections skip the API
        if (includesAll) {
          _allPlanYearsCache.value = accountOwnerPlanYears.value
          _allPlanYearsCacheLoadedAt.value = Date.now()
        }

        console.log(`Loaded ${planYears.length} plan years with ${uniqueDates.length} unique start dates`)
      } else {
        accountOwnerPlanYears.value = []
        planYearStartDates.value = []
        filteredPlanYears.value = []
      }

    } catch (error) {
      handleErrors('loadPlanYearsByAccountOwner', error)
      accountOwnerPlanYears.value = []
      planYearStartDates.value = []
      filteredPlanYears.value = []
    } finally {
      isLoadingPlanYears.value = false
    }
  }

  /**
   * Load filter options from Firestore questions collection
   * @param {string} questionId - The ID of the question document
   * @returns {Promise<Array>} Array of { value, label } options
   */
  const loadQuestionOptions = async (questionId) => {
    try {
      const db = getFirestore()
      const questionsRef = collection(db, 'questions')
      const questionQuery = query(questionsRef, where('__name__', '==', questionId))
      const questionSnapshot = await getDocs(questionQuery)

      if (questionSnapshot.empty) {
        console.error(`Question ${questionId} not found`)
        return []
      }

      const questionData = questionSnapshot.docs[0].data()
      const values = questionData?.options?.values || []

      // Map array to { value: index, label: text } format
      return values.map((label, index) => ({
        value: index,
        label: label
      }))

    } catch (error) {
      console.error(`Error loading question ${questionId} options:`, error)
      return []
    }
  }

  /**
   * Load all filter options
   * @returns {Promise<void>}
   */
  const loadAllFilters = async () => {
    isLoadingFilters.value = true
    try {
      // Load all filters in parallel (they don't depend on each other)
      const [, erisaPlansResult, medicalPlansResult, coveragesResult] = await Promise.all([
        loadAccountOwners(),
        loadQuestionOptions('8'),  // ERISA Plan
        loadQuestionOptions('11'), // Medical Plan
        loadQuestionOptions('12')  // Coverages
      ])

      // Set the loaded options
      erisaPlans.value = erisaPlansResult || []
      medicalPlans.value = medicalPlansResult || []
      coveragesOptions.value = coveragesResult || []
    } catch (error) {
      console.error('Error loading filters:', error)
    } finally {
      isLoadingFilters.value = false
    }
  }

  /**
   * Returns the default account owner selection (array) for filters.
   * If the logged-in user exists in the account owners list, returns their email;
   * otherwise falls back to 'all' (e.g. when the user belongs to an excluded domain).
   */
  const getDefaultAccountOwner = () => {
    const currentEmail = getCurrentUserEmail()
    const existsInList = !!currentEmail && accountOwners.value.some(o => o.value === currentEmail)
    return existsInList ? [currentEmail] : ['all']
  }

  /**
   * Reset all filters to their default values
   */
  const resetFilters = () => {
    Object.assign(selectedFilters.value, {
      accountOwner: getDefaultAccountOwner(),
      planYearStartDate: getActivePlanYearDates(),
      erisaPlan: [],
      fullTimeEmployees: '',
      medicalPlan: [],
      coverages: []
    })
  }

  /**
   * Check if Action Center data should be reloaded from the backend.
   * Compares local localDataLoadTimestamp with the agency's last_updated in Realtime DB.
   * @returns {Promise<boolean>} True if data should be reloaded
   */
  const shouldReloadData = async () => {
    // If data was never loaded reload
    if (!localDataLoadTimestamp.value || !currentAgencyId.value) return true

    try {
      const { getAgencyLastUpdated } = AgencyDataService()
      const lastUpdated = await getAgencyLastUpdated(currentAgencyId.value)

      if (!lastUpdated) return false

      // Reload if agency data was updated after the last load
      return lastUpdated > localDataLoadTimestamp.value
    } catch (error) {
      handleErrors('ActionCenterStore.shouldReloadData', error)
      return true
    }
  }


  /**
   * Clear all loaded filter data
   */
  const clearFilterData = () => {
    accountOwners.value = []
    accountOwnerPlanYears.value = []
    filteredPlanYears.value = []
    planYearStartDates.value = []
    erisaPlans.value = []
    medicalPlans.value = []
    coveragesOptions.value = []
    resetBookDetailsView()
  }

  /**
   * Full store reset for logout / session change.
   * Clears every piece of state so no data leaks between accounts.
   */
  const resetStore = () => {
    accountOwners.value = []
    accountOwnerPlanYears.value = []
    filteredPlanYears.value = []
    planYearStartDates.value = []
    erisaPlans.value = []
    medicalPlans.value = []
    coveragesOptions.value = []
    isLoadingFilters.value = false
    isLoadingPlanYears.value = true
    showHighRiskDollarView.value = false
    lastRemindersSummary.value = null
    remindersBannerVisible.value = false
    shareUpdateEmailSent.value = false
    wasNext30DaysFilterActive.value = false
    historicalReminders.value = []
    isLoadingHistoricalReminders.value = false
    savedScrollPosition.value = 0
    savedDeadlinesSelection.value = null
    bookDetailsView.value = DEFAULT_VIEW
    accountOwnerCountsRequestId.value = 0
    currentAgencyId.value = null
    localDataLoadTimestamp.value = null
    _allPlanYearsCache.value = []
    _allPlanYearsCacheLoadedAt.value = 0
    selectedFilters.value = {
      accountOwner: [],
      planYearStartDate: [],
      erisaPlan: [],
      fullTimeEmployees: '',
      medicalPlan: [],
      coverages: []
    }
  }

  /**
   * Toggle between High-Risk percentage and dollar amount views
   */
  const toggleHighRiskDollarView = () => {
    showHighRiskDollarView.value = !showHighRiskDollarView.value
  }

  const setLastRemindersSummary = (summary, next30DaysActive = false) => {
    lastRemindersSummary.value = summary
    wasNext30DaysFilterActive.value = next30DaysActive
    if (summary && Array.isArray(summary.details) && summary.details.length > 0) {
      remindersBannerVisible.value = true
    } else {
      remindersBannerVisible.value = false
    }
  }

  const setRemindersBannerVisible = (val) => {
    remindersBannerVisible.value = val
  }

  const setShareUpdateEmailSent = (val) => {
    shareUpdateEmailSent.value = val
  }

  /**
   * Load historical reminders for the filtered plan years
   * Queries reminders_sent collection by plan_year_id
   */
  const loadHistoricalReminders = async () => {
    const planYearIds = filteredPlanYears.value
      .map(py => py.id)
      .filter(Boolean)

    if (planYearIds.length === 0) {
      historicalReminders.value = []
      return
    }

    isLoadingHistoricalReminders.value = true
    try {
      const db = getFirestore()
      const remindersRef = collection(db, 'reminders_sent')
      
      // Firestore 'in' queries are limited to 30 items, so we batch if needed
      const batches = []
      for (let i = 0; i < planYearIds.length; i += 30) {
        batches.push(planYearIds.slice(i, i + 30))
      }

      const allReminders = []
      for (const batch of batches) {
        const q = query(remindersRef, where('plan_year_id', 'in', batch))
        const snapshot = await getDocs(q)
        snapshot.forEach(doc => {
          allReminders.push({ id: doc.id, ...doc.data() })
        })
      }

      // Sort by created_at descending (most recent first)
      allReminders.sort((a, b) => {
        const dateA = new Date(a.created_at || 0)
        const dateB = new Date(b.created_at || 0)
        return dateB - dateA
      })

      historicalReminders.value = allReminders
    } catch (error) {
      historicalReminders.value = []
    } finally {
      isLoadingHistoricalReminders.value = false
    }
  }

  return {
    // State
    accountOwners,
    accountOwnerPlanYears,
    filteredPlanYears,
    planYearStartDates,
    erisaPlans,
    medicalPlans,
    coveragesOptions,
    isLoadingFilters,
    isLoadingPlanYears,
    selectedFilters,
    showHighRiskDollarView,
    lastRemindersSummary,
    remindersBannerVisible,
    shareUpdateEmailSent,
    wasNext30DaysFilterActive,
    historicalReminders,
    isLoadingHistoricalReminders,
    savedScrollPosition,
    savedDeadlinesSelection,
    bookDetailsView,
    
    // Cache state
    currentAgencyId,
    localDataLoadTimestamp,
    
    // Actions
    getCurrentUserEmail,
    getDefaultAccountOwner,
    shouldReloadData,
    isActivePlanYearDate,
    getActivePlanYearDates,
    getGroupedPlanYearStartDates,
    loadAccountOwners,
    loadPlanYearsByAccountOwner,
    loadQuestionOptions,
    loadAllFilters,
    applyFilters,
    resetFilters,
    clearFilterData,
    resetStore,
    setBookDetailsView,
    resetBookDetailsView,
    toggleHighRiskDollarView,
    setLastRemindersSummary,
    setRemindersBannerVisible,
    setShareUpdateEmailSent,
    loadHistoricalReminders
  }
})
